import { Component, Ref, Vue } from 'vue-property-decorator';
import DataTable, { TableVisibility } from '@/components/data-table/DataTable';
import { Options } from '@/components/mi-dialog/MiDialog';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import SupplierSizeCreateOrUpdateDialog from '@/components/CustomDialog/SupplierSizeCreateOrUpdateDialog/SupplierSizeCreateOrUpdateDialog.vue';
import { SupplierSize } from '@/models/SupplierSize';

@Component<SettingsSupplierSizes>({
  components: {
    SupplierSizeCreateOrUpdateDialog,
  },
})
export default class SettingsSupplierSizes extends Vue {
  @Ref()
  protected supplierSizesDatatable!: DataTable;

  protected selectedSupplierSize: null | SupplierSize = null;

  protected isCreatingOrUpdatingSupplierSize: boolean = false;

  protected supplierSizesTableVisibility: TableVisibility = {
    title: false,
    checkboxes: false,
    search: true,
  }

  protected getMaisonSizeNameFromSupplierSize(supplierSize: SupplierSize): string {
    return supplierSize
      && supplierSize.size
      && supplierSize.size.name
        ? supplierSize.size.name
        : '-';
  }

  protected onIsCreatingSupplierSizeInput(): void {
    this.selectedSupplierSize = null;
  }

  protected onSupplierSizeCreated(): void {
    this.refreshSupplierSizesDatatable();
  }

  protected onSupplierSizeUpdated(): void {
    this.refreshSupplierSizesDatatable();
  }

  protected refreshSupplierSizesDatatable(): void {
    if (! this.supplierSizesDatatable) { return; }

    this.supplierSizesDatatable.refresh(true);
  }

  protected async deleteSupplierSize(): Promise<null | SupplierSize> {
    if (!this.selectedSupplierSize) { return null; }

    const selectedSupplierSize = await this.selectedSupplierSize
      .delete()
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    return selectedSupplierSize;
  }

  protected get deleteSupplierSizeDialogOptions(): Options {
    return {
      title: this.selectedSupplierSizeName || 'Confirm deletion',
      text: 'Are you sure you wish to delete this supplier size?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Delete',
          action: async () => {
            const selectedSupplierSize = await this.deleteSupplierSize();

            if (! selectedSupplierSize) { return; }

            this.selectedSupplierSize = null;

            this.refreshSupplierSizesDatatable();
          },
        },
        cancel: {
          text: 'Cancel',
          action: () => (this.selectedSupplierSize = null),
        },
      },
    };
  }

  protected get selectedSupplierSizeName(): string {
    return this.selectedSupplierSize
      ? this.selectedSupplierSize.name || ''
      : '';
  }

  protected get supplierSizesTableOptions(): Object {
    return {
      model: new SupplierSize()
        .include('size'),
      name: {
        singular: 'Supplier size',
        plural: 'Supplier sizes',
      },
      sort: {
        key: 'sort_column',
        order: 'ASC',
      },
      headers: [
        {
          text: 'Supplier size',
          value: 'name',
          sortable: {
            key: 'name',
            order: 'ASC',
          },
        },
        {
          text: 'Maison size',
          value: 'size__name',
          sortable: {
            key: 'size__name',
            order: 'ASC',
          },
          transform: (size__name: undefined, supplierSize: SupplierSize) => this.getMaisonSizeNameFromSupplierSize(supplierSize),
        },
        {
          text: 'Sort column',
          value: 'sort_column',
          sortable: {
            key: 'sort_column',
            order: 'ASC',
          },
        },
        {
          text: 'Default order quantity',
          value: 'default_order_quantity',
          sortable: {
            key: 'default_order_quantity',
            order: 'ASC',
          },
        },
      ],
      actions: [
        {
          type: 'edit',
          label: 'edit',
          icon: 'edit',
          action: (datatable: DataTable, supplierSize: SupplierSize) => {
            this.selectedSupplierSize = supplierSize;

            this.isCreatingOrUpdatingSupplierSize = true;
          },
        },
        {
          type: 'delete',
          label: 'delete',
          icon: 'delete',
          action: (datatable: DataTable, supplierSize: SupplierSize) => {
            this.selectedSupplierSize = supplierSize;

            this.$store.dispatch('openDialog', this.deleteSupplierSizeDialogOptions);
          },
        },
      ],
      add: () => (this.isCreatingOrUpdatingSupplierSize = true)
    };
  }
}
